import { apiGet, apiPost, apiPatch, apiDelete } from '@/api/client';
import type {
  AdminUserRow,
  AdminUserDetail,
  CreateUserPayload,
  UpdateUserPayload,
  AdminCompanyRow,
  AdminCompanyDetail,
  AdminBuildingRow,
  TooltipEntry,
} from '@/types/admin';
import type { CompanyFeatureFlags } from '@/types/auth';

// ---------- Drawing Vault grant writes (US-DVA-02) -------------------------

/** `'add'` upserts and leaves out-of-set grants alone; `'replace'` revokes anything omitted. */
export type DvGrantMode = 'add' | 'replace';

export type DvGrantRowStatus = 'granted' | 'unchanged' | 'revoked' | 'rejected';

/** One building's outcome. `reason` is populated only on `'rejected'`. */
export interface DvGrantRowResult {
  buildingId: number;
  status: DvGrantRowStatus;
  reason?: string;
}

/** Row-level result of `POST /admin/users/:userId/buildings`. */
export interface DvGrantApplyResult {
  mode: DvGrantMode;
  granted: number;
  unchanged: number;
  revoked: number;
  rejected: number;
  results: DvGrantRowResult[];
}

// ---------- Users ----------------------------------------------------------

export const adminUsersApi = {
  list(params?: {
    companyId?: number;
    role?: string;
    status?: string;
    search?: string;
  }): Promise<AdminUserRow[]> {
    return apiGet<AdminUserRow[]>('/admin/users', params);
  },

  detail(userId: number): Promise<AdminUserDetail> {
    return apiGet<AdminUserDetail>(`/admin/users/${userId}`);
  },

  create(data: CreateUserPayload): Promise<AdminUserDetail> {
    return apiPost<AdminUserDetail>('/admin/users', data);
  },

  update(userId: number, data: UpdateUserPayload): Promise<AdminUserDetail> {
    return apiPatch<AdminUserDetail>(`/admin/users/${userId}`, data);
  },

  remove(userId: number): Promise<void> {
    return apiDelete<void>(`/admin/users/${userId}`);
  },

  getBuildings(userId: number): Promise<number[]> {
    return apiGet<number[]>(`/admin/users/${userId}/buildings`);
  },

  /**
   * Apply a Drawing Vault building grant set (US-DVA-02).
   *
   * `mode` defaults server-side to `'replace'`, which revokes anything omitted. Pass `'add'`
   * for the additive bulk grant — it leaves grants outside `buildingIds` untouched and is
   * idempotent on replay. Rejected ids (not DV-enabled) come back per row rather than
   * failing the whole call.
   */
  setBuildings(
    userId: number,
    buildingIds: number[],
    mode?: DvGrantMode,
  ): Promise<DvGrantApplyResult> {
    return apiPost<DvGrantApplyResult>(`/admin/users/${userId}/buildings`, {
      buildingIds,
      ...(mode ? { mode } : {}),
    });
  },
};

// ---------- Companies ------------------------------------------------------

export const adminCompaniesApi = {
  list(): Promise<AdminCompanyRow[]> {
    return apiGet<AdminCompanyRow[]>('/admin/companies');
  },

  detail(companyId: number): Promise<AdminCompanyDetail> {
    return apiGet<AdminCompanyDetail>(`/admin/companies/${companyId}`);
  },

  updateFlags(companyId: number, flags: CompanyFeatureFlags): Promise<AdminCompanyDetail> {
    return apiPatch<AdminCompanyDetail>(`/admin/companies/${companyId}/flags`, flags);
  },
};

// ---------- Buildings ------------------------------------------------------

export const adminBuildingsApi = {
  list(companyId?: number): Promise<AdminBuildingRow[]> {
    return apiGet<AdminBuildingRow[]>('/admin/buildings', companyId ? { companyId } : undefined);
  },

  users(buildingId: number): Promise<AdminUserRow[]> {
    return apiGet<AdminUserRow[]>(`/admin/buildings/${buildingId}/users`);
  },
};

// ---------- Tooltips -------------------------------------------------------

export interface TooltipUpsertPayload {
  key: string;
  routePattern?: string | null;
  targetSelector?: string | null;
  title?: string | null;
  body: string;
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
  tourName?: string | null;
  tourOrder?: number | null;
  isActive?: boolean;
}

export const adminTooltipsApi = {
  list(): Promise<TooltipEntry[]> {
    return apiGet<TooltipEntry[]>('/admin/tooltips');
  },

  upsert(data: TooltipUpsertPayload): Promise<TooltipEntry> {
    return apiPost<TooltipEntry>('/admin/tooltips', data);
  },

  remove(tooltipId: number): Promise<void> {
    return apiDelete<void>(`/admin/tooltips/${tooltipId}`);
  },
};

// ---------- Public tooltips (read-only, consumed by app users) ------------

export const tooltipsApi = {
  list(routePattern?: string): Promise<TooltipEntry[]> {
    return apiGet<TooltipEntry[]>('/tooltips', routePattern ? { routePattern } : undefined);
  },

  markSeen(tooltipId: number): Promise<void> {
    return apiPost<void>(`/tooltips/${tooltipId}/seen`);
  },

  tourProgress(tourName: string): Promise<{ completed: boolean; lastStep: number }> {
    return apiGet<{ completed: boolean; lastStep: number }>(`/tooltips/tours/${encodeURIComponent(tourName)}/progress`);
  },

  setTourProgress(
    tourName: string,
    data: { completed: boolean; lastStep: number },
  ): Promise<void> {
    return apiPost<void>(`/tooltips/tours/${encodeURIComponent(tourName)}/progress`, data);
  },
};
